import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useInView } from "motion/react";
import { IconLoader2 } from "@tabler/icons-react";
import {
  apiClient,
  revalidateApiQuery,
  useApiInfiniteQuery,
  useApiQuery,
} from "../../../services/api";
import type { Api } from "../../../services/api";
import { formatArticleDate } from "../../../domains/app/utils/renderDate";
import { Button } from "../../../components/ui/Button";
import { ChatInput } from "../../../components/ui/ChatInput";
import {
  ChatOptions,
  type ChatOption,
} from "../../../components/ui/ChatOptions";
import { PublicationCard } from "../../../components/ui/PublicationCard";
import { ScrollDownButton } from "../../../components/ui/ScrollDownButton";
import SkeletonPublicationCard from "../../../components/skeleton/SkeletonPublicationCard";
import { ConversationBubble } from "../components/ConversationBubble";
import { ChatMessageCard } from "../../../components/ui/ChatMessageCard";
import { useMeasuredHeight } from "../../../hooks/use-measured-height";
import { useScrollPinToBottom } from "../../../hooks/use-scroll-pin-to-bottom";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { preferenceConversationStore } from "../../../domains/auth/stores/onboardingConversationStore";
import {
  getCurrentUserData,
  replaceCurrentUserData,
} from "../../../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { getArticleTypeVariant } from "../../../domains/app/utils/getArticleTypeText";
import { displayToast } from "../../../components/app/AppToast";
import { gaEventRelevantTopicsSubmitted } from "../../../tools/analytics/domains/onboarding.analytics";
import { mixpanelSetUserProperties } from "../../../tools/analytics/base.analytics";
import { authorLine } from "../../../domains/article/utils/authorLine";

type TurnKind = "topics" | "feed-taste";

// The topics turn offers options from two sources — the specialty list
// (`/topics`, which also carries `treePrefix`/`inSpecialty`) and the learned
// layer (`/users/me/interests`) — and uses only the uid + label of either. So
// the option shape is the narrower of the two, and the wider one flows in
// structurally; no field is invented to make them match.
type SelectedTopic =
  Api["get"]["/webapp/private/users/me/interests"]["output"]["body"]["topics"][number];
type MeshInterest =
  Api["get"]["/webapp/private/users/me/interests"]["output"]["body"]["meshInterests"][number];
type ResolveResult =
  Api["post"]["/webapp/private/topics/resolve"]["output"]["body"];
type InterpretResult =
  Api["post"]["/webapp/private/topics/interpret"]["output"]["body"];
type InterpretedTopic = InterpretResult["topics"][number];
type AddedItem =
  | { kind: "topic"; uid: string; name: string; selected: boolean }
  | {
      kind: "mesh";
      descriptorUi: string;
      name: string;
      treeNumbers: string[];
      isMajor: boolean | null;
      selected: boolean;
    };

const SUGGESTED_LIMIT = 5;
const FEED_FETCH_LIMIT = 10;
const FEED_PREVIEW_COUNT = 3;

const PROGRESSIVE_BLUR_LAYERS = [
  { blur: "backdrop-blur-[2px]", mask: "transparent 0%, black 45%" },
  { blur: "backdrop-blur-[5px]", mask: "transparent 30%, black 75%" },
  { blur: "backdrop-blur-[10px]", mask: "transparent 55%, black 95%" },
] as const;

/**
 * The feed-preference flow rendered as ONE growing conversation: each turn's
 * agent message and the user's answer accumulate in a scrollable thread (nothing
 * is erased), while the active turn's interactive widget lives in the composer
 * band at the bottom. Reached from the Discovery "Customize your feed" nudge —
 * signup does NOT run it. The final action calls `onFinish`.
 */
function ProfilingConversationContent({
  headerInset,
  specialtyId,
  roomCount,
  finishing,
  onFinish,
  suggested,
  learnedTopics,
  initialTopics,
  initialMeshInterests,
}: {
  headerInset: number;
  specialtyId: string | null;
  roomCount: number;
  finishing: boolean;
  onFinish: () => void;
  // The options offered on the topics turn, learned-first (see the parent). A
  // subset of them — `learnedTopics` — are the conversation-learned ones, which
  // arrive PRE-SELECTED; the rest are the specialty seed list, which do not.
  suggested: SelectedTopic[];
  learnedTopics: SelectedTopic[];
  initialTopics: SelectedTopic[];
  initialMeshInterests: MeshInterest[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const { contentLang } = useActiveLang();
  const composer = useMeasuredHeight();
  const scrollRef = useRef<HTMLDivElement>(null);
  // The feed-taste "Explore my feed" button reveals when this end-of-thread
  // sentinel is near the viewport and hides again once the user scrolls up
  // ~160px past the end (the positive bottom margin is that hysteresis).
  const endSentinelRef = useRef<HTMLDivElement>(null);
  const reachedEnd = useInView(endSentinelRef, { margin: "0px 0px 160px 0px" });

  const turns: TurnKind[] = ["topics", "feed-taste"];
  // Resume from a reload: the session store mirrors each turn's answer, so start
  // at the first turn without a stored answer — an answered `topics` resumes
  // straight to `feed-taste`, which has no answer of its own. A fresh visit
  // reads an empty store and starts at `topics`.
  const persistedAnswers = preferenceConversationStore.get();
  const [turnIndex, setTurnIndex] = useState(() => {
    const firstUnanswered = turns.findIndex((kind) =>
      kind === "feed-taste" ? true : persistedAnswers[kind] === null
    );
    return firstUnanswered === -1 ? 0 : firstUnanswered;
  });
  const [answers, setAnswers] = useState<Partial<Record<TurnKind, string>>>(
    () =>
      persistedAnswers.topics === null
        ? {}
        : { topics: persistedAnswers.topics }
  );
  // The active turn's composer widget is hidden until its agent message has
  // finished typing (reset on every `advance`, revealed by the bubble's
  // `onDone`) — so options fade in after the question, not at mount.
  const [composerRevealed, setComposerRevealed] = useState(false);
  const activeTurn = turns[turnIndex];

  function advance(kind: TurnKind, answer: string): void {
    setAnswers((prev) => ({ ...prev, [kind]: answer }));
    setComposerRevealed(false);
    setTurnIndex((index) => index + 1);
    // Mirror the answer so a reload resumes at the next turn. `feed-taste` never
    // advances (its CTA finishes the flow), so it is never written here.
    if (kind !== "feed-taste") {
      preferenceConversationStore.set((prev) => ({ ...prev, [kind]: answer }));
    }
  }

  // ── Topics turn ────────────────────────────────────────────────
  const initialTopicUids = new Set(initialTopics.map((topic) => topic.uid));
  const learnedTopicUids = new Set(learnedTopics.map((topic) => topic.uid));
  const [selectedSuggested, setSelectedSuggested] = useState<Set<string>>(
    new Set(
      suggested
        .filter(
          (topic) =>
            // Already declared → keep it checked (the refresh confirms the
            // existing set). Learned from the conversation → check it too: the
            // user is confirming what Juisci understood, not starting over.
            // Everything else (the specialty seed list) stays unchecked.
            initialTopicUids.has(topic.uid) || learnedTopicUids.has(topic.uid)
        )
        .map((topic) => topic.uid)
    )
  );
  const [addedItems, setAddedItems] = useState<AddedItem[]>([
    ...initialTopics
      .filter(
        (topic) => !suggested.some((suggestion) => suggestion.uid === topic.uid)
      )
      .map((topic) => ({
        kind: "topic" as const,
        uid: topic.uid,
        name: getItemTranslation(topic),
        selected: true,
      })),
    ...initialMeshInterests.map((interest) => ({
      kind: "mesh" as const,
      descriptorUi: interest.descriptorUi,
      name: interest.name,
      treeNumbers: interest.treeNumbers,
      isMajor: interest.isMajor,
      selected: true,
    })),
  ]);
  const [showChatInput, setShowChatInput] = useState(false);
  const [chatText, setChatText] = useState("");
  const [resolving, setResolving] = useState(false);
  const [savingTopics, setSavingTopics] = useState(false);
  // Free-text-first agent: "input" shows the ChatInput, "confirm" shows the
  // grounded topics (pre-selected) in a ChatOptions for adjustment. On a
  // confident interpretation we skip "confirm" entirely and advance.
  const [topicsPhase, setTopicsPhase] = useState<"input" | "confirm">("input");
  const [inputText, setInputText] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [userTopicsText, setUserTopicsText] = useState<string | null>(null);
  const [agentMessage, setAgentMessage] = useState<string | null>(null);
  const optionId = (item: AddedItem): string =>
    item.kind === "topic" ? item.uid : item.descriptorUi;

  // Re-anchor to the bottom on each new turn, once a message finishes typing,
  // whenever the composer band is re-measured, and on each topics sub-step
  // (user answer / thinking / agent reply / confirm) — all grow the thread after
  // layout, so the layout-effect pin (not a call-site scroll) keeps the newest
  // content clear of the composer. Frozen on feed-taste (manual scroll).
  useScrollPinToBottom(
    scrollRef,
    `${turnIndex}:${composerRevealed}:${composer.height}:${topicsPhase}:${userTopicsText !== null}:${interpreting}:${agentMessage !== null}`,
    activeTurn !== "feed-taste"
  );

  function toggleSuggested(uid: string): void {
    setSelectedSuggested((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }
  function toggleAdded(index: number): void {
    setAddedItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item
      )
    );
  }
  // `selected` decides whether a resolved item arrives CHECKED. A manually typed
  // "add own topic" and an already-declared topic arrive checked (`true`); the
  // agent's interpreted/connected suggestions arrive UNCHECKED (`false`) — the
  // user opts in rather than pruning boxes we ticked for them.
  function addResolved(result: ResolveResult, selected: boolean): void {
    switch (result.kind) {
      case "topic": {
        const topic = result.topic;
        if (suggested.some((s) => s.uid === topic.uid)) {
          if (selected)
            setSelectedSuggested((prev) => new Set(prev).add(topic.uid));
          return;
        }
        // Dedup by uid, OR-ing the checked state: a later manual add
        // (`selected: true`) of a topic an earlier interpret seeded unchecked
        // upgrades it to checked; an interpret never un-checks an existing one.
        setAddedItems((prev) =>
          prev.some((i) => i.kind === "topic" && i.uid === topic.uid)
            ? prev.map((i) =>
                i.kind === "topic" && i.uid === topic.uid
                  ? { ...i, selected: i.selected || selected }
                  : i
              )
            : [...prev, { kind: "topic", ...topic, selected }]
        );
        return;
      }
      case "topics": {
        // An expansion — a specialty's seed map, or the topics an umbrella MeSH
        // descriptor covers — each carrying the caller's `selected` default.
        for (const topic of result.topics) {
          addResolved({ kind: "topic", topic }, selected);
        }
        return;
      }
      case "mesh": {
        const descriptor = result.descriptor;
        setAddedItems((prev) =>
          prev.some(
            (i) =>
              i.kind === "mesh" && i.descriptorUi === descriptor.descriptorUi
          )
            ? prev.map((i) =>
                i.kind === "mesh" && i.descriptorUi === descriptor.descriptorUi
                  ? { ...i, selected: i.selected || selected }
                  : i
              )
            : [
                ...prev,
                {
                  kind: "mesh",
                  ...descriptor,
                  isMajor: null,
                  selected,
                },
              ]
        );
        return;
      }
      case "none":
        displayToast(
          t("Couldn't find a matching topic. Try another term."),
          "info"
        );
        return;
      default:
        throw new Error(result satisfies never);
    }
  }
  async function handleTopicSend(): Promise<void> {
    const text = chatText.trim();
    if (text === "" || resolving) return;
    setResolving(true);
    try {
      const { body } = await apiClient.post("/webapp/private/topics/resolve", {
        json: { text, specialtyId },
      });
      // A manual "add own topic" is an explicit choice → arrives checked.
      addResolved(body, true);
      if (body.kind !== "none") {
        setChatText("");
        setShowChatInput(false);
      }
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setResolving(false);
    }
  }
  const topicOptions: ChatOption[] = [
    ...suggested.map((topic) => ({
      id: topic.uid,
      label: getItemTranslation(topic),
    })),
    ...addedItems.map((item) => ({ id: optionId(item), label: item.name })),
  ];
  const selectedTopicIds = [
    ...suggested
      .filter((topic) => selectedSuggested.has(topic.uid))
      .map((topic) => topic.uid),
    ...addedItems.filter((item) => item.selected).map(optionId),
  ];
  function handleSelectTopic(id: string): void {
    if (suggested.some((topic) => topic.uid === id)) {
      toggleSuggested(id);
      return;
    }
    const index = addedItems.findIndex((item) => optionId(item) === id);
    if (index !== -1) toggleAdded(index);
  }
  // Persist a confirmed set of interests (topics + mesh) to the user profile.
  // Returns whether it succeeded — the caller advances only on success so a
  // failed PUT never silently drops the user's answer.
  async function persistSelection(sel: {
    topicUids: string[];
    meshInterests: {
      descriptorUi: string;
      name: string;
      treeNumbers: string[];
      isMajor: boolean | null;
    }[];
    names: string[];
  }): Promise<boolean> {
    gaEventRelevantTopicsSubmitted({
      flow: "preference_refresh",
      total_found_topics: suggested.length,
      total_confirmed_topics: sel.topicUids.length + sel.meshInterests.length,
      confirmed_topics: sel.names,
      total_suggested_topics: learnedTopics.length,
      total_suggested_confirmed: sel.topicUids.filter((uid) =>
        learnedTopicUids.has(uid)
      ).length,
    });
    mixpanelSetUserProperties({ confirmed_topics: sel.names });

    setSavingTopics(true);
    try {
      await apiClient.put("/webapp/private/users/me/interests", {
        json: {
          topicUids: sel.topicUids,
          meshInterests: sel.meshInterests,
        },
      });
      void revalidateApiQuery("/webapp/private/playlists/recommended");
      await Promise.all([
        revalidateApiQuery("/webapp/private/users/me/topics", {}),
        revalidateApiQuery("/webapp/private/users/me/interests", {}),
        revalidateApiQuery("/webapp/private/feed"),
      ]);
      const cachedUser = getCurrentUserData();
      if (cachedUser !== null) {
        replaceCurrentUserData({
          ...cachedUser,
          meshInterests: sel.meshInterests,
        });
      }
      return true;
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      setSavingTopics(false);
      return false;
    }
  }

  function currentSelection(): {
    topicUids: string[];
    meshInterests: {
      descriptorUi: string;
      name: string;
      treeNumbers: string[];
      isMajor: boolean | null;
    }[];
    names: string[];
  } {
    const suggestedUids = suggested
      .filter((topic) => selectedSuggested.has(topic.uid))
      .map((topic) => topic.uid);
    const addedTopicUids = addedItems.flatMap((item) =>
      item.kind === "topic" && item.selected ? [item.uid] : []
    );
    const meshInterests = addedItems.flatMap((item) =>
      item.kind === "mesh" && item.selected
        ? [
            {
              descriptorUi: item.descriptorUi,
              name: item.name,
              treeNumbers: item.treeNumbers,
              isMajor: item.isMajor,
            },
          ]
        : []
    );
    const names = [
      ...suggested
        .filter((topic) => selectedSuggested.has(topic.uid))
        .map((topic) => getItemTranslation(topic)),
      ...addedItems.filter((item) => item.selected).map((item) => item.name),
    ];
    return {
      topicUids: [...new Set([...suggestedUids, ...addedTopicUids])],
      meshInterests,
      names,
    };
  }

  function advanceTopics(names: string[]): void {
    advance(
      "topics",
      names.length > 0 ? names.join(", ") : t("I'll skip topics for now")
    );
  }

  // Seed a grounded guess into the confirm-step selection model with the given
  // checked state: a curated topic that matches a suggestion toggles it, anything
  // else becomes an added row. Reuses the `/resolve` merge logic. The interpret
  // path passes `selected: false` so connected guesses are proposed, not ticked.
  function seedGuessed(item: InterpretedTopic, selected: boolean): void {
    if (item.kind === "topic")
      addResolved(
        { kind: "topic", topic: { uid: item.uid, name: item.name } },
        selected
      );
    else
      addResolved(
        {
          kind: "mesh",
          descriptor: {
            descriptorUi: item.descriptorUi,
            name: item.name,
            treeNumbers: item.treeNumbers,
          },
        },
        selected
      );
  }

  async function handleInterpretSend(): Promise<void> {
    const text = inputText.trim();
    if (text === "" || interpreting) return;
    setUserTopicsText(text);
    setInputText("");
    setInterpreting(true);
    setComposerRevealed(false);
    try {
      const { body } = await apiClient.post(
        "/webapp/private/topics/interpret",
        { json: { text, specialtyId } }
      );
      const message =
        body.message.trim().length > 0
          ? body.message
          : t("Here's what I picked up. Adjust anything below.");
      for (const topic of body.topics) {
        seedGuessed(topic, false);
      }
      setTopicsPhase("confirm");
      setAgentMessage(message);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      setAgentMessage(null);
      setUserTopicsText(null);
      setComposerRevealed(true);
    } finally {
      setInterpreting(false);
    }
  }

  // Fired when the agent's reply finishes typing — reveal the pre-selected
  // ChatOptions the user adjusts before confirming.
  function handleAgentMessageDone(): void {
    setComposerRevealed(true);
  }

  async function handleTopicsContinue(): Promise<void> {
    const sel = currentSelection();
    const ok = await persistSelection(sel);
    if (ok) advanceTopics(sel.names);
  }

  // ── Feed-taste turn ────────────────────────────────────────────
  // Fetch the feed ONLY once the user reaches the feed-taste turn — by then
  // `persistSelection` has already saved the chosen topics to `topicInterests`,
  // so the server ranks against them. Firing earlier (at mount, on the topics
  // turn) would race the save: the query key is only {limit, language}, nothing
  // invalidates it after topics persist, and the server would fall back to the
  // topic-less cold-start ranking (freshest × quality) — surfacing off-profile
  // content (e.g. a Neurology paper for a melanoma/heart-failure user). The
  // gate keeps the query idle until then; the skeleton covers the fetch.
  const feedQuery = useApiInfiniteQuery(
    "/webapp/private/feed",
    activeTurn !== "feed-taste"
      ? false
      : { query: { limit: FEED_FETCH_LIMIT, language: contentLang } }
  );
  const previewArticles =
    feedQuery.state === "success"
      ? feedQuery.docs
          .filter((doc) => doc.content_format === "Article")
          .slice(0, FEED_PREVIEW_COUNT)
      : [];
  // The feed can fail to load, or come back with nothing to preview — fold
  // both into a single "unavailable" state so the preview and the Explore
  // button never wait on content that will never arrive.
  const feedPreview: FeedPreviewState =
    feedQuery.state === "pending"
      ? { status: "loading" }
      : feedQuery.state === "error" || previewArticles.length === 0
        ? { status: "unavailable" }
        : { status: "ready", articles: previewArticles };

  function agentText(kind: TurnKind): string {
    switch (kind) {
      case "topics":
        return t(
          "Your profile's **all set**. Let's build your feed. First, which **topics** should I keep you across?"
        );
      case "feed-taste":
        return roomCount > 0
          ? t(
              "Perfect, your feed is ready.\n\nI've lined up your topics and {{count}} Rooms. Here's a taste of your feed.",
              { count: roomCount }
            )
          : t(
              "Perfect, your feed is ready.\n\nI've lined up your topics. Here's a taste of your feed."
            );
      default:
        throw new Error(kind satisfies never);
    }
  }

  return (
    <>
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-y-auto px-5"
        style={{ paddingTop: headerInset, paddingBottom: composer.height }}
      >
        <div className="flex flex-col gap-4 pt-6 pb-1">
          {turns.slice(0, turnIndex + 1).map((kind, index) => {
            const isActive = index === turnIndex;
            // The topics turn is a mini free-text exchange: question →
            // user's words → the agent's thinking + reply. The reply's `onDone`
            // (not the question's) drives the reveal/advance once answered.
            if (kind === "topics")
              return (
                <div key={kind} className="flex flex-col gap-4">
                  <ConversationBubble
                    text={agentText(kind)}
                    instant={!isActive}
                    onDone={
                      isActive && agentMessage === null
                        ? () => {
                            setComposerRevealed(true);
                          }
                        : null
                    }
                  />
                  {userTopicsText !== null && (
                    <ChatMessageCard variant="default">
                      {userTopicsText}
                    </ChatMessageCard>
                  )}
                  {interpreting && <TypingIndicator />}
                  {agentMessage !== null && (
                    <ConversationBubble
                      text={agentMessage}
                      instant={!isActive}
                      onDone={isActive ? handleAgentMessageDone : null}
                    />
                  )}
                  {/* Restored from a reload: the live free-text exchange state
                      (userTopicsText/agentMessage) is gone, so show the persisted
                      answer summary instead of an empty question. */}
                  {!isActive &&
                    userTopicsText === null &&
                    answers[kind] !== undefined && (
                      <ChatMessageCard variant="default">
                        {answers[kind]}
                      </ChatMessageCard>
                    )}
                </div>
              );
            return (
              <div key={kind} className="flex flex-col gap-4">
                <ConversationBubble
                  text={agentText(kind)}
                  instant={!isActive}
                  onDone={
                    isActive
                      ? () => {
                          setComposerRevealed(true);
                        }
                      : null
                  }
                />
                {kind === "feed-taste" && (
                  <FeedPreview
                    state={feedPreview}
                    contentLang={contentLang}
                    eyebrow={t("From your feed")}
                    unavailableMessage={t(
                      "Couldn't load the preview. Your feed is ready to explore."
                    )}
                  />
                )}
                {answers[kind] !== undefined && (
                  <ChatMessageCard variant="default">
                    {answers[kind]}
                  </ChatMessageCard>
                )}
              </div>
            );
          })}
          <div ref={endSentinelRef} className="h-px w-full" />
        </div>
      </div>

      <div
        ref={composer.ref}
        className="absolute inset-x-0 bottom-0 z-20 px-4 pb-[calc(var(--safe-area-bottom)+0.5rem)]"
      >
        {/* Scroll-to-bottom hint: on the feed-taste turn the preview + "Explore
            my feed" button live below the fold, so nudge the user down until
            the end sentinel is in view (`reachedEnd`), which is also when the
            Explore button fades in. Hidden when there's no preview to scroll. */}
        {activeTurn === "feed-taste" && (
          <ScrollDownButton
            visible={feedPreview.status !== "unavailable" && !reachedEnd}
            onClick={() => {
              const el = scrollRef.current;
              if (el === null) return;
              el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            }}
            className="absolute -top-12 left-1/2 -translate-x-1/2 z-10"
          />
        )}
        {/* Fade + slide the composer as the active turn changes, so each new
            question's widget animates in from below (and the old one out).
            Held back until `composerRevealed` so it appears once the agent
            message has finished typing, not at mount. */}
        <AnimatePresence mode="wait" initial={false}>
          {composerRevealed && (
            <motion.div
              key={activeTurn}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {activeTurn === "topics" && (
                <AnimatePresence mode="wait" initial={false}>
                  {topicsPhase === "input" ? (
                    <motion.div
                      key="topics-input"
                      initial={{ y: 24, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 24, opacity: 0 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                      <ChatInput
                        mode={null}
                        leading={null}
                        leadingPanel={null}
                        banner={null}
                        attach={null}
                        dictation={null}
                        stream={null}
                        autoFocus
                        value={inputText}
                        onChange={(e) => {
                          setInputText(e.target.value);
                        }}
                        placeholder={t("Ask anything")}
                        onSend={() => {
                          void handleInterpretSend();
                        }}
                        sendDisabled={interpreting || inputText.trim() === ""}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="topics-confirm"
                      initial={{ y: 24, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 24, opacity: 0 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                      <ChatOptions
                        title={
                          // Name where the pre-checked options come from. Without
                          // it, topics the user never typed appear already
                          // selected and read as a system that decided for them.
                          learnedTopics.length === 0
                            ? t("Do you have any more topics to add?")
                            : t("Based on your conversations with Juisci")
                        }
                        select="multiple"
                        options={topicOptions}
                        selectedIds={selectedTopicIds}
                        onSelect={handleSelectTopic}
                        freeText={{
                          kind: "inline",
                          label: t("Add own topics"),
                          open: showChatInput,
                          onOpen: () => {
                            setShowChatInput(true);
                          },
                          placeholder: t("Add topic"),
                          value: chatText,
                          onChange: (value) => {
                            setChatText(value);
                          },
                          onSubmit: () => {
                            void handleTopicSend();
                          },
                          sendDisabled: resolving || chatText.trim() === "",
                        }}
                        onContinue={() => {
                          void handleTopicsContinue();
                        }}
                        continueLabel={t("Continue")}
                        continueDisabled={savingTopics}
                        onClose={null}
                        pager={null}
                        placement="band"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {activeTurn === "feed-taste" && (
                <motion.div
                  className="pb-4"
                  initial={{ opacity: 0, y: 24 }}
                  // Without a preview there is nothing to scroll through, so
                  // the button must not wait on the end-of-thread sentinel —
                  // it is the only way out of onboarding.
                  animate={
                    reachedEnd || feedPreview.status === "unavailable"
                      ? { opacity: 1, y: 0 }
                      : { opacity: 0, y: 24 }
                  }
                  transition={{ type: "spring", stiffness: 320, damping: 32 }}
                >
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={finishing}
                    leftIcon={
                      finishing ? (
                        <IconLoader2 className="animate-spin" />
                      ) : null
                    }
                    onClick={onFinish}
                  >
                    {t("Explore my feed")}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export function ProfilingConversation({
  headerInset,
  specialtyId,
  roomCount,
  finishing,
  onFinish,
}: {
  headerInset: number;
  specialtyId: string | null;
  roomCount: number;
  finishing: boolean;
  onFinish: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const topicsInput = {
    query: { specialtyId: specialtyId === null ? undefined : specialtyId },
  };
  const topicsQuery = useApiQuery("/webapp/private/topics", topicsInput);
  const interestsQuery = useApiQuery("/webapp/private/users/me/interests", {
    keepPreviousData: true,
  });

  if (topicsQuery.state === "pending" || interestsQuery.state === "pending") {
    return (
      <div className="flex h-full items-center justify-center">
        <IconLoader2 className="animate-spin" />
      </div>
    );
  }

  if (topicsQuery.state === "error" || interestsQuery.state === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <p>{t("Something went wrong. Please try again.")}</p>
        <Button
          variant="secondary"
          onClick={() => {
            void topicsQuery.refetch();
            void interestsQuery.refetch();
          }}
        >
          {t("Retry")}
        </Button>
      </div>
    );
  }

  // Learned-first suggestion list. The conversation-learned topics (head of the
  // UPP layer, already stripped of what the user declared) lead and arrive
  // pre-selected; the specialty seed list fills the remaining slots and stays
  // unchecked. Capped at SUGGESTED_LIMIT overall so the card does not grow.
  const learned = interestsQuery.body.learnedTopics;
  const learnedUids = new Set(learned.map((topic) => topic.uid));
  const suggestedTopics = [
    ...learned,
    ...topicsQuery.body.filter((topic) => !learnedUids.has(topic.uid)),
  ].slice(0, SUGGESTED_LIMIT);

  return (
    <ProfilingConversationContent
      headerInset={headerInset}
      specialtyId={specialtyId}
      roomCount={roomCount}
      finishing={finishing}
      onFinish={onFinish}
      suggested={suggestedTopics}
      learnedTopics={learned}
      initialTopics={interestsQuery.body.topics}
      initialMeshInterests={interestsQuery.body.meshInterests}
    />
  );
}

/** Three-dot "the agent is thinking" bubble, shown while `/interpret` runs. */
function TypingIndicator(): React.JSX.Element {
  return (
    <div className="flex items-center gap-1 self-start px-1 py-2">
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className="size-2 rounded-full bg-ds-slate-500/60 motion-safe:animate-bounce"
          style={{ animationDelay: `${dot * 0.15}s` }}
        />
      ))}
    </div>
  );
}

type FeedArticle = Extract<
  Api["get"]["/webapp/private/feed"]["output"]["body"]["docs"][number],
  { content_format: "Article" }
>;

type FeedPreviewState =
  | { status: "loading" }
  | { status: "unavailable" }
  | { status: "ready"; articles: FeedArticle[] };

function FeedPreview({
  state,
  contentLang,
  eyebrow,
  unavailableMessage,
}: {
  state: FeedPreviewState;
  contentLang: string;
  eyebrow: string;
  unavailableMessage: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3 border-t border-solid border-ds-slate-100 pt-4">
      <p className="font-['DM_Sans',sans-serif] text-[13px]/5 font-semibold uppercase tracking-[0.5px] text-ds-slate-500">
        {eyebrow}
      </p>
      {state.status === "loading" ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: FEED_PREVIEW_COUNT }).map((_value, index) => (
            <SkeletonPublicationCard showImage key={index} showDescription />
          ))}
        </div>
      ) : state.status === "unavailable" ? (
        <p className="text-sm text-ds-slate-500">{unavailableMessage}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {state.articles.map((item, index) => {
            const journal = item.journal;
            const cardImage = item.cardImage;
            const imageUrl =
              cardImage === null
                ? null
                : cardImage.sizes === null
                  ? cardImage.url
                  : cardImage.sizes.medium.file;
            const description =
              item.tldr !== null && item.tldr.length > 0 ? item.tldr : null;
            const roomReco =
              item.recommendation.reason === "fromRoom"
                ? item.recommendation
                : null;
            const card = (
              <PublicationCard
                isInPlayer={false}
                className="bg-transparent md:hover:bg-transparent"
                title={item.title}
                journalName={journal === null ? null : journal.name}
                author={authorLine(item.primary_author, item.authors)}
                date={formatArticleDate(item.publication_date, contentLang)}
                meta={
                  item.article_type === null ? "Article" : item.article_type
                }
                metaVariant={getArticleTypeVariant(item.article_type)}
                access={item.isOpenAccess === true ? "open" : "closed"}
                recommendation={roomReco}
                isNew
                isBookmarked={false}
                onBookmark={null}
                onShare={null}
                onClick={null}
                onJournalClick={null}
                onRecommendationClick={null}
                imageUrl={imageUrl}
                description={description}
              />
            );
            const isTeaser = index === state.articles.length - 1;
            if (!isTeaser) return <div key={item.id}>{card}</div>;
            return (
              <div key={item.id} className="relative">
                {card}
                <div className="pointer-events-none absolute inset-y-0 -inset-x-5">
                  {PROGRESSIVE_BLUR_LAYERS.map((layer) => (
                    <div
                      key={layer.blur}
                      className={`absolute inset-0 ${layer.blur}`}
                      style={{
                        maskImage: `linear-gradient(to bottom, ${layer.mask})`,
                        WebkitMaskImage: `linear-gradient(to bottom, ${layer.mask})`,
                      }}
                    />
                  ))}
                  {/* Fades into the page background — anything else (white)
                      paints a visible band the conversation never had. */}
                  <div className="absolute inset-0 bg-linear-to-b from-transparent from-70% to-ds-cream" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
